import type React from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardCard from "../../../ui/DashboardCard/DashboardCard.tsx";
import Button from "../../../ui/Button/Button";
import type { AdultDashboardPostedGigItemResponse } from "../../../../types/adultDashboardTypes.ts";
import { formatCityStateZip } from "../../../../utils/LocationUtils";
import { formatGigCrewSummary } from "../../../../utils/GigCrewUtils";
import { getPostedGigPrimaryText, toReadableDate } from "../shared/dashboardDisplay";

type AdultPostedGigsBoxProps = {
  postedGigs: AdultDashboardPostedGigItemResponse[];
  loading: boolean;
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  onCreateGigClick?: () => void;
};

const getPostedGigLink = (gig: AdultDashboardPostedGigItemResponse) => {
  const view = gig.status === "DRAFT" || gig.status === "EXPIRED" ? gig.status : "PUBLISHED";
  return `/adult/gigs?view=${view}&gigId=${encodeURIComponent(gig.gigId)}`;
};

const isInteractiveTarget = (target: EventTarget | null) => {
  return target instanceof HTMLElement && Boolean(target.closest("a, button, input, select, textarea, [role='button']"));
};

const AdultPostedGigsBox = ({
  postedGigs,
  loading,
  title = "Posted Gigs",
  subtitle = "Gigs you haven't accepted a teen for yet.",
  emptyMessage = "No posted gigs yet. Post your first gig to start getting applicants.",
  onCreateGigClick,
}: AdultPostedGigsBoxProps) => {
  const navigate = useNavigate();
  const showEmptyCreateAction = !loading && postedGigs.length === 0 && Boolean(onCreateGigClick);
  const cardLink = postedGigs.length === 0 && !showEmptyCreateAction ? "/adult/gigs" : undefined;
  const opensAllPostedGigs = !loading && postedGigs.length > 0;

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!opensAllPostedGigs || event.defaultPrevented || isInteractiveTarget(event.target)) {
      return;
    }

    navigate("/adult/gigs");
  };

  return (
    <DashboardCard
      title={title}
      subtitle={subtitle}
      to={cardLink}
      className={opensAllPostedGigs ? "cursor-pointer" : undefined}
      onClick={handleCardClick}
    >
      {loading ? (
        <p className="text-sm text-myteengig-text-muted">Loading posted gigs...</p>
      ) : postedGigs.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-myteengig-text-muted">{emptyMessage}</p>
          {onCreateGigClick ? (
            <Button type="button" size="sm" onClick={onCreateGigClick}>
              Create Gig
            </Button>
          ) : null}
        </div>
      ) : (
        <ul className="space-y-3">
          {postedGigs.map((gig) => (
            <li key={gig.gigId}>
              <Link
                to={getPostedGigLink(gig)}
                className="block rounded-lg border border-myteengig-border bg-myteengig-background p-3 transition-colors hover:border-myteengig-primary hover:bg-white"
              >
                <div>
                  <p className="font-semibold text-myteengig-text-primary">{getPostedGigPrimaryText(gig)}</p>
                </div>
                <div className="mt-2 flex flex-col gap-1 text-sm text-myteengig-text-muted sm:flex-row sm:items-center sm:justify-between">
                  <span>{formatCityStateZip(gig)}</span>
                  <span>{gig.applicantCount} applicants</span>
                </div>
                <p className="mt-1 text-xs font-medium text-myteengig-text-primary">{formatGigCrewSummary(gig)}</p>
                <p className="mt-1 text-xs text-myteengig-text-muted">Posted on {toReadableDate(gig.createdAt)}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
};

export default AdultPostedGigsBox;
